import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { confirmPasswordReset } from "../api/api";
import ValphaLogo from "../branding/logo";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const hasResetToken = Boolean(uid && token);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setError("");
    if (password !== passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmPasswordReset({
        uid,
        token,
        new_password: password,
        new_password_confirm: passwordConfirm,
      });
      setIsComplete(true);
    } catch (err) {
      setError(err?.message || "This reset link is invalid or has expired.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#0F1826] font-inter text-white overflow-hidden px-4 sm:px-6">
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0F1826]/90 to-[#0F1826]/90" />
      <div className="relative z-20 bg-[#1A2539]/80 backdrop-blur-md p-5 sm:p-8 rounded-xl shadow-lg w-full max-w-[340px] sm:max-w-[384px]">
        <div className="flex flex-col items-center mb-6 space-y-3 text-center">
          <img
            src={ValphaLogo}
            alt="valpha.io"
            className="h-14 sm:h-16 w-auto object-contain bg-transparent select-none"
          />
          <h1 className="text-2xl font-bold tracking-tight">
            {isComplete ? "Password reset" : "Choose a new password"}
          </h1>
          <p className="text-sm text-gray-400">
            {isComplete
              ? "Your new password is ready to use."
              : "Use at least 8 characters and avoid common passwords."}
          </p>
        </div>

        {!hasResetToken ? (
          <div className="space-y-5 text-center">
            <p role="alert" className="text-sm leading-6 text-red-400">
              This password reset link is incomplete or invalid.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex bg-[#4FA9A1] hover:bg-[#3e938b] transition-colors text-[#0F1826] font-semibold py-2 px-6 rounded-lg text-sm"
            >
              Request a new link
            </Link>
          </div>
        ) : isComplete ? (
          <div className="space-y-5 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-400/15 text-green-400">
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
              </svg>
            </div>
            <Link
              to="/login"
              className="inline-flex bg-[#4FA9A1] hover:bg-[#3e938b] transition-colors text-[#0F1826] font-semibold py-2 px-6 rounded-lg text-sm"
            >
              Go to Login
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <p role="alert" className="text-red-400 text-center mb-4 text-sm">
                {error}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <span className="sr-only">New password</span>
                <input
                  type="password"
                  placeholder="New password"
                  className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="sr-only">Confirm new password</span>
                <input
                  type="password"
                  placeholder="Confirm new password"
                  className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
                  value={passwordConfirm}
                  onChange={(event) => setPasswordConfirm(event.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </label>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#4FA9A1] hover:bg-[#3e938b] disabled:cursor-not-allowed disabled:opacity-60 transition-colors text-[#0F1826] font-semibold py-2 rounded-lg"
              >
                {isSubmitting ? "Resetting..." : "Reset password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
